interface FormatDateProps {
  showYear?: boolean
  showTime?: boolean
}
export const isDayLightSavingMode = (date: Date) => {
  const usFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    timeZoneName: 'short'
  })

  const dateInfo = usFormatter.formatToParts(date)

  const timeZoneName = dateInfo.find((part) => part.type === 'timeZoneName')

  if (!timeZoneName) return false

  return timeZoneName.value === 'EDT'
}

export const ALLOWED_MINUTES_OFFSET = 5

/**
 * Returns the next minute from now based on next valid minutes [0, 15, 30, 45]
 * @returns Date
 * @example
 * getNextMinimumValidDateTime('2024-08-10T14:05:00.000Z') // '2024-08-10T14:15:00.000Z'
 * getNextMinimumValidDateTime('2024-08-10T14:10:00.000Z') // '2024-08-10T14:30:00.000Z'
 * getNextMinimumValidDateTime('2024-08-10T14:42:00.000Z') // '2024-08-10T15:00:00.000Z'
 * getNextMinimumValidDateTime('2024-08-10T10:27:00.000Z') // '2024-08-10T10:45:00.000Z'
 * @description We get the next minute from the current minute and check if the difference is not closer to ALLOWED_MINUTES_OFFSET
 */
export const getNextMinimumValidDateTime = () => {
  const date = new Date()
  const validMinutes = [0, 15, 30, 45]
  const minutes = date.getMinutes()
  const index = validMinutes.findIndex((ele) => ele > minutes)
  let nextMinutes = index >= 0 ? validMinutes[index] : 60
  const diff = nextMinutes - minutes

  date.setSeconds(0)
  date.setMilliseconds(0)

  if (diff > ALLOWED_MINUTES_OFFSET) {
    date.setMinutes(nextMinutes)
    return date
  }

  nextMinutes = validMinutes[(index + 1) % 4]

  if (index === -1) {
    nextMinutes = validMinutes[1]
  }

  if (index === -1 || index === 3) {
    date.setHours(date.getHours() + 1)
  }

  date.setMinutes(nextMinutes)

  return date
}

export const getFirstDayOfNextWeek = (fromDate: Date) => {
  const dayOfWeek = fromDate.getDay() // get the current day of the week (0 = Sunday, 6 = Saturday)
  const daysUntilNextWeek = 7 - dayOfWeek // calculate how many days until the start of the next week
  const nextWeekStart = new Date(
    fromDate.getFullYear(),
    fromDate.getMonth(),
    fromDate.getDate() + daysUntilNextWeek + 1,
    0,
    0,
    0,
    0
  ) // get the first day of next week (Monday)

  return nextWeekStart
}

/**
 * Formats a date to 'MMM DD, HH:MM AM/PM PST' format
 * @param date - date string
 * @returns 'MMM DD, HH:MM AM/PM PST'
 * @example
 * monthDayAndTimeFormatter('2021-10-01T00:00:00.000Z') // 'Oct 1, 12:00 AM PST'
 * monthDayAndTimeFormatter('2021-10-10T00:00:00.000Z') // 'Oct 10, 12:00 AM PST'
 * monthDayAndTimeFormatter('2021-10-10T12:00:00.000Z') // 'Oct 10, 12:00 PM PST'
 * */
export const monthDayAndTimeFormatter = (date: Date) => {
  return formatDate(date, { showYear: false, showTime: true })
}

const formatDate = (date: Date, options: FormatDateProps) => {
  const { showYear, showTime } = options

  // Format the month
  const formatter = new Intl.DateTimeFormat('en', { month: 'short' })
  const month = formatter.format(date)

  // Extract day, year, hours, and minutes
  const day = date.getDate()
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes()

  // Format minutes with leading zero if needed
  const formattedMinutes = minutes.toString().padStart(2, '0')

  // Construct the date string
  let formattedDate = `${month} ${day}${showYear ? `, ${year}` : ''}`

  if (showTime) {
    // Format hours to 12-hour format
    const hour12 = hours % 12 || 12
    const period = hours >= 12 ? 'PM' : 'AM'
    formattedDate += `, ${hour12}:${formattedMinutes} ${period}`
  }

  return formattedDate
}

export const convertUtcToCst = (utcDateStr: string) => {
  const utcDate = new Date(utcDateStr)
  // Use 'America/Chicago' timezone to get CST/CDT
  return new Date(utcDate.toLocaleString('en-US', { timeZone: 'America/Chicago' }))
}

/**
 * Converts a timestamp in seconds to ISO format
 * @param timestamp - timestamp in seconds
 * @returns ISO format date string
 **/
export const convertTimestampToISO = (timestamp: number) => {
  // Convert from seconds to milliseconds
  const date = new Date(timestamp * 1000)
  return date.toISOString()
}

/**
 * Returns the timestamp of the start and end of the period based on the start timestamp
 * @param startPeriodTimestamp - start timestamp in milliseconds
 * @returns object with start and end timestamps in seconds
 **/
export function getPeriodTimestamps(startPeriodTimestamp: number): { start: number; end: number } {
  const startDate = new Date(startPeriodTimestamp)
  const endDate = new Date(startDate)
  endDate.setMonth(startDate.getMonth() + 1)
  return {
    start: Math.floor(startDate.getTime() / 1000), // Start timestamp in seconds
    end: Math.floor(endDate.getTime() / 1000) // End timestamp in seconds
  }
}

export const isSameYearMonthAndDay = (date1: Date, date2: Date) => {
  // log years
  // log months
  // log days
  console.log(date1.getFullYear(), date2.getFullYear())
  console.log(date1.getMonth(), date2.getMonth())
  console.log(date1.getDate(), date2.getDate())
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}
